import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Toaster } from "sonner";
import SectionReveal from "./SectionReveal";
import WeaverLogo from "./WeaverLogo";
import OnboardingModal from "./OnboardingModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const STEPS = [
  {
    n: "01",
    title: "Subscribe",
    body: "One flat rate of $6,500/month. No hourly billing, no per-project quotes, no surprise invoices.",
  },
  {
    n: "02",
    title: "Request",
    body: "Send us any project from the production levels below. Stack as many requests as you like — we keep an organized queue.",
  },
  {
    n: "03",
    title: "We Produce",
    body: "Your active project gets our full-time focus — a dedicated 40-hour work week — until it's approved. Then the next project in your queue starts immediately.",
  },
  {
    n: "04",
    title: "Review & Approve",
    body: "Every draft is delivered through Vimeo.com for time-stamped feedback. Revisions are unlimited — we refine until you sign off.",
  },
];

const INCLUDED = [
  {
    title: "One active project, unlimited queue",
    body: "Projects run one at a time, back to back. Queue up as much as you want; there's always something in production.",
  },
  {
    title: "Unlimited revisions",
    body: "We revise every project until you approve it. No revision-round counting, no change-order fees.",
  },
  {
    title: "Collaborative scriptwriting & creative direction",
    body: "We work with your team to develop the script for every project — story structure, messaging, and creative direction shaped together, not an upsell.",
  },
  {
    title: "Standard stock footage & music licensing",
    body: "Licensed stock and music are included. Premium licenses, if ever needed, are billed at cost with your approval first.",
  },
  {
    title: "Platform-ready delivery",
    body: "Thumbnails, captions and subtitles, and exports formatted for every platform you publish on.",
  },
  {
    title: "Full ownership",
    body: "You own everything — final deliverables and the underlying project files, transferred on request.",
  },
  {
    title: "Weekly check-in calls (optional)",
    body: "Prefer async? Everything runs through Vimeo.com. Want facetime? We'll hold a standing weekly call.",
  },
];

const LEVELS = [
  {
    level: "01",
    service: "Short-Form Social",
    turnaround: "1–2 business days per video",
    scope:
      "Reels, TikToks, Shorts, cutdowns, and other social videos under ~90 seconds. Client footage or stock, captions, music, simple graphics.",
  },
  {
    level: "02",
    service: "Standard Video Editing",
    turnaround: "3–5 business days per 3 minutes of finished video",
    scope:
      "Social media content, standard corporate videos, testimonials, event highlights. Clean cuts, transitions, stock music, simple graphics.",
  },
  {
    level: "03",
    service: "High-End Commercial & Broadcast",
    turnaround: "5–7 business days per minute of finished video",
    scope:
      "TV commercials, broadcast content, premium corporate videos, high-end marketing. Advanced editing, motion graphics, color grading.",
  },
  {
    level: "04",
    service: "Video + Animation Hybrid",
    turnaround: "5–7 business days per 30 seconds of finished video",
    scope:
      "Explainer videos with animated overlays, branded social content, enhanced commercials. Live footage combined with motion graphics.",
  },
  {
    level: "05",
    service: "Pure Animation",
    turnaround:
      "7–10 business days per 30 seconds of finished animation, following storyboard approval",
    scope:
      "Motion graphics, character animation, animated explainers, custom illustration. Pre-production (script, storyboard, style frames) typically adds 1–2 weeks.",
  },
  {
    level: "06",
    service: "Music Composition",
    turnaround: "5 business days per minute of produced music",
    scope:
      "Bespoke music composed and produced for your content — custom scores, brand themes, soundtracks for any project above. Original work you own, with no stock licensing limits.",
  },
];

const FAQ = [
  {
    q: "How is this different from a freelancer or a traditional agency?",
    a: "Freelancers juggle multiple clients and disappear mid-project; agencies quote every project separately and bill for every revision. Per-project work also carries its own overhead: every video means a new estimate to review, a PO to open, a contract to route, and an invoice to reconcile. A subscription replaces all of that with one predictable line item — request a project and it goes straight into production. Your subscription buys dedicated, continuous capacity at a fixed cost: the economics of an employee with the flexibility of a vendor.",
  },
  {
    q: "What if I don't have footage?",
    a: "Levels 4 and 5 (animation) require no footage at all. For edited video, we can build from licensed stock footage, screen recordings, or footage from a local production company — we're happy to coordinate specs with whoever shoots for you. And don't underestimate what you can capture yourself: we can take interviews filmed on your own camera or phone and change backgrounds, re-light, and apply other effects to bring the footage to professional standards.",
  },
  {
    q: "Who owns the work?",
    a: "You do — fully. Final videos and source project files are yours, including if you cancel. One condition applies to third-party stock: stock footage and music are licensed under Weaver Media's accounts and cover their use within your final deliverables only. Those assets can't be extracted from project files and reused as standalone clips in other productions — new uses require a new license, which we'll handle for any project you run through us.",
  },
  {
    q: "What counts as one project?",
    a: "One deliverable at one production level — e.g., one 3-minute video, one 30-second animation. A batch of short-form videos from the same source footage can often be grouped and turned around together; we'll scope that with you when you make the request.",
  },
  {
    q: "What do you need from me to keep things fast?",
    a: "Organized footage, a clear brief (references welcome), brand assets up front, and consolidated feedback within a business day of each draft. The clearer the input, the faster the output.",
  },
];

const BILLING = [
  {
    title: "$6,500/month, flat",
    body: "Billed monthly in advance.",
  },
  {
    title: "Month-to-month",
    body: "No long-term contract, no onboarding fee.",
  },
  {
    title: "Cancel anytime",
    body: "Cancel before your next billing date and you won't be charged again. Work in progress is completed through the end of your paid period.",
  },
  {
    title: "Your subscription reserves capacity",
    body: "Because our production time is dedicated to you all month, the subscription runs continuously whether or not there's an active project — days spent waiting on briefs, footage, or feedback aren't credited or refunded. The best way to get full value is simple: keep your queue stocked, and we'll never stop producing.",
  },
];

const Eyebrow = ({ children, testId }) => (
  <span
    className="block text-xs font-bold uppercase tracking-[0.3em] text-zinc-500 mb-6"
    data-testid={testId}
  >
    {children}
  </span>
);

const Subscription = () => {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      data-testid="subscription-page"
    >
      {/* Minimal header */}
      <header className="w-full px-6 sm:px-12 py-6 sm:py-8 flex items-center justify-between border-b border-white/5">
        <Link to="/" data-testid="subscription-logo-link" className="focus:outline-none">
          <WeaverLogo size="md" data-testid="subscription-page-logo" />
        </Link>
        <Link
          to="/"
          data-testid="subscription-back-home"
          className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-zinc-400 hover:text-white transition-colors duration-300"
        >
          <ArrowLeft
            size={14}
            className="transition-transform duration-500 group-hover:-translate-x-1"
          />
          Back
        </Link>
      </header>

      <main>
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 sm:px-12 pt-20 sm:pt-28 pb-24 sm:pb-32">
          <div className="max-w-4xl">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="block text-xs font-bold uppercase tracking-[0.3em] text-zinc-500 mb-6"
            >
              — The Subscription
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-none text-white"
              data-testid="subscription-headline"
            >
              Your in-house
              <br />
              content team.
              <br />
              <span className="text-zinc-500">Without the overhead.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 text-base sm:text-lg font-light text-zinc-400 tracking-wide leading-relaxed max-w-2xl"
              data-testid="subscription-intro"
            >
              Hiring an in-house video editor, motion designer, and
              post-production supervisor means salaries, benefits, software,
              equipment, and management time. The Weaver Media subscription
              gives you the same capability as a flat monthly line item: a
              dedicated, senior-level remote production team that works your
              projects full time, one after another, for as long as you
              subscribe.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6"
            >
              <button
                onClick={() => setContactOpen(true)}
                data-testid="subscription-hero-cta"
                className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-white text-black font-bold text-xs sm:text-sm uppercase tracking-[0.25em] hover:bg-zinc-200 transition-all duration-300"
              >
                Start Subscription Onboarding
                <ArrowUpRight
                  size={18}
                  className="transition-transform duration-500 group-hover:rotate-45"
                />
              </button>
              <div data-testid="subscription-price-tag">
                <div className="text-2xl sm:text-3xl font-black text-white">
                  $6,500<span className="text-zinc-500">/mo</span>
                </div>
                <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                  Flat · Month-to-month · Cancel anytime
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* How it works */}
        <section className="border-t border-white/5 bg-[#0A0A0A] py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <SectionReveal>
              <Eyebrow testId="how-it-works-eyebrow">01 — How It Works</Eyebrow>
              <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-white">
                Request. <span className="text-zinc-500">Produce. Repeat.</span>
              </h2>
            </SectionReveal>
            <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
              {STEPS.map((s, i) => (
                <SectionReveal
                  key={s.n}
                  delay={i * 0.1}
                  className="bg-[#0A0A0A] p-8 sm:p-10"
                  data-testid={`step-${s.n}`}
                >
                  <div className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-600">
                    {s.n}
                  </div>
                  <h3 className="mt-4 text-xl sm:text-2xl font-bold uppercase tracking-tight text-white">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                    {s.body}
                  </p>
                </SectionReveal>
              ))}
            </div>
          </div>
        </section>

        {/* What's included */}
        <section className="py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
              <SectionReveal className="md:col-span-5">
                <Eyebrow testId="included-eyebrow">02 — What&apos;s Included</Eyebrow>
                <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight leading-[0.95] text-white">
                  Everything,
                  <br />
                  <span className="text-zinc-500">every month</span>
                </h2>
              </SectionReveal>
              <SectionReveal className="md:col-span-7" delay={0.15}>
                <ul className="divide-y divide-white/10 border-t border-b border-white/10">
                  {INCLUDED.map((item) => (
                    <li
                      key={item.title}
                      className="py-6 flex gap-5"
                      data-testid={`included-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <Check size={18} className="mt-1 shrink-0 text-white" />
                      <div>
                        <div className="text-sm sm:text-base font-bold uppercase tracking-wide text-white">
                          {item.title}
                        </div>
                        <p className="mt-2 text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                          {item.body}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </SectionReveal>
            </div>
          </div>
        </section>

        {/* Production levels */}
        <section className="border-t border-white/5 bg-[#0A0A0A] py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <SectionReveal>
              <Eyebrow testId="levels-eyebrow">03 — Production Levels</Eyebrow>
              <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-white">
                Six levels. <span className="text-zinc-500">One rate.</span>
              </h2>
              <p className="mt-8 text-base sm:text-lg font-light text-zinc-400 tracking-wide leading-relaxed max-w-3xl">
                Every request falls into one of six production levels.
                Turnaround estimates begin when we have your footage, brief, and
                brand assets in hand, and assume feedback within one business
                day of each draft. Complex briefs may shift estimates — we&apos;ll
                always confirm a timeline before work begins.
              </p>
            </SectionReveal>

            <div className="mt-16 grid gap-px bg-white/10 border border-white/10">
              {LEVELS.map((l, i) => (
                <SectionReveal
                  key={l.level}
                  delay={Math.min(i * 0.08, 0.4)}
                  className="bg-[#0A0A0A] p-8 sm:p-10 grid sm:grid-cols-12 gap-6 sm:gap-10"
                  data-testid={`level-${l.level}`}
                >
                  <div className="sm:col-span-1 text-2xl sm:text-3xl font-black text-zinc-700">
                    {l.level}
                  </div>
                  <div className="sm:col-span-3">
                    <h3 className="text-lg sm:text-xl font-bold uppercase tracking-tight text-white leading-snug">
                      {l.service}
                    </h3>
                  </div>
                  <div className="sm:col-span-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">
                      Turnaround
                    </div>
                    <p className="text-sm font-medium text-zinc-200 leading-relaxed">
                      {l.turnaround}
                    </p>
                  </div>
                  <div className="sm:col-span-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">
                      Examples &amp; Scope
                    </div>
                    <p className="text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                      {l.scope}
                    </p>
                  </div>
                </SectionReveal>
              ))}
            </div>

            <SectionReveal delay={0.1}>
              <div className="mt-10 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                <div className="bg-[#111] p-8 sm:p-10">
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-3">
                    Brand Integration · Add 1–2 Business Days
                  </div>
                  <p className="text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                    Want your own brand graphics woven into the work — logos,
                    fonts, lower thirds, title cards, existing templates? Plan
                    for one to two additional days for ingest, reconstruction,
                    and bespoke design of your brand elements. This setup only
                    happens once: after your brand system is built into our
                    pipeline, every subsequent project uses it with no added
                    time.
                  </p>
                </div>
                <div className="bg-[#111] p-8 sm:p-10">
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-3">
                    What Does a Month Look Like?
                  </div>
                  <p className="text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                    As a rough guide, one month typically produces: 10–20
                    short-form social videos, or 4–6 standard 3-minute videos,
                    or one broadcast-quality commercial campaign with cutdowns,
                    or a 60–90 second animated explainer — or any mix, worked
                    back to back.
                  </p>
                </div>
              </div>
            </SectionReveal>
          </div>
        </section>

        {/* Billing + exclusions */}
        <section className="py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <SectionReveal>
              <Eyebrow testId="billing-eyebrow">04 — Billing &amp; Commitment</Eyebrow>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mb-10">
                Simple terms
              </h2>
              <ul className="divide-y divide-white/10 border-t border-b border-white/10">
                {BILLING.map((b) => (
                  <li key={b.title} className="py-6">
                    <div className="text-sm sm:text-base font-bold uppercase tracking-wide text-white">
                      {b.title}
                    </div>
                    <p className="mt-2 text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                      {b.body}
                    </p>
                  </li>
                ))}
              </ul>
            </SectionReveal>

            <SectionReveal delay={0.15}>
              <Eyebrow testId="exclusions-eyebrow">05 — What&apos;s Not Included</Eyebrow>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mb-10">
                Clear boundaries
              </h2>
              <ul className="divide-y divide-white/10 border-t border-b border-white/10">
                <li className="py-6">
                  <div className="text-sm sm:text-base font-bold uppercase tracking-wide text-white">
                    Filming / production services
                  </div>
                  <p className="mt-2 text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                    This is a remote post-production subscription. You provide
                    the footage (or we work from licensed stock); we handle
                    everything after the shoot. That said, we&apos;ll help you get
                    there: we can build from licensed stock, screen recordings,
                    or footage from a local production company — and coordinate
                    specs with whoever shoots for you. We can even take
                    interviews filmed on your own camera or phone and change
                    backgrounds, re-light, and apply effects to bring them to
                    professional standards.
                  </p>
                </li>
                <li className="py-6">
                  <div className="text-sm sm:text-base font-bold uppercase tracking-wide text-white">
                    Rush and same-day turnarounds
                  </div>
                  <p className="mt-2 text-sm font-light text-zinc-400 tracking-wide leading-relaxed">
                    Our turnaround estimates reflect our standard pace at
                    full-time focus. Same-day or overnight delivery isn&apos;t part
                    of the subscription; ask us about rush availability for
                    genuine emergencies.
                  </p>
                </li>
              </ul>
            </SectionReveal>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-white/5 bg-[#0A0A0A] py-24 sm:py-32">
          <div className="max-w-4xl mx-auto px-6 sm:px-12">
            <SectionReveal>
              <Eyebrow testId="faq-eyebrow">06 — FAQ</Eyebrow>
              <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-white">
                Questions, <span className="text-zinc-500">answered</span>
              </h2>
            </SectionReveal>
            <SectionReveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-12">
                {FAQ.map((f, i) => (
                  <AccordionItem
                    key={f.q}
                    value={`faq-${i}`}
                    className="border-white/10"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger className="py-6 text-sm sm:text-base font-bold uppercase tracking-wide text-white hover:no-underline hover:text-zinc-300 [&>svg]:text-zinc-500">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-8 text-sm sm:text-base font-light text-zinc-400 tracking-wide leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </SectionReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 sm:py-36">
          <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
            <SectionReveal>
              <Eyebrow testId="cta-eyebrow">07 — Get Started</Eyebrow>
              <h2 className="text-4xl sm:text-6xl font-black uppercase tracking-tight leading-[0.95] text-white">
                Never stop
                <br />
                <span className="text-zinc-500">producing</span>
              </h2>
            </SectionReveal>
            <SectionReveal delay={0.15}>
              <div className="mt-14 flex justify-center">
                <button
                  onClick={() => setContactOpen(true)}
                  data-testid="subscription-footer-cta"
                  className="group inline-flex items-center justify-center gap-3 w-full sm:w-auto px-10 py-5 bg-white text-black font-bold text-xs sm:text-sm uppercase tracking-[0.25em] hover:bg-zinc-200 transition-all duration-300"
                >
                  Start Subscription Onboarding
                  <ArrowUpRight
                    size={18}
                    className="transition-transform duration-500 group-hover:rotate-45"
                  />
                </button>
              </div>
              <p className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-zinc-600">
                $6,500/mo · Flat · Cancel anytime
              </p>
            </SectionReveal>
          </div>
        </section>
      </main>

      <OnboardingModal open={contactOpen} onOpenChange={setContactOpen} />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 0,
            fontFamily: "Montserrat, sans-serif",
            fontSize: "12px",
            letterSpacing: "0.05em",
          },
        }}
      />
    </div>
  );
};

export default Subscription;
